// frontend/src/pages/admin/EngagementBoard.tsx
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import type { Leak, EngagementStatus } from "../../types/leak";
import { getEngagementBoard, updateEngagementStatus } from "../../api/engagement";
import Skeleton from "../../components/Skeleton";

/* ── constants ──────────────────────────────────────────────────────────── */
const ALL_STAGES: EngagementStatus[] = [
    "leak_detected",
    "client_notified",
    "awaiting_approval",
    "approved",
    "engagement_active",
    "resolved",
    "declined",
];

const STAGE_LABELS: Record<EngagementStatus, string> = {
    leak_detected: "Leak Detected",
    client_notified: "Client Notified",
    awaiting_approval: "Awaiting Approval",
    approved: "Approved",
    engagement_active: "Active",
    resolved: "Resolved",
    declined: "Declined",
};

const ALLOWED_TRANSITIONS: Record<string, EngagementStatus[]> = {
    leak_detected: ["client_notified"],
    client_notified: ["awaiting_approval"],
    awaiting_approval: ["approved", "declined"],
    approved: ["engagement_active"],
    engagement_active: ["resolved"],
    declined: [],
    resolved: [],
};

function timeAgo(dateStr?: string): string {
    if (!dateStr) return "";
    const ms = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(ms / (1000 * 60 * 60));
    if (hours < 1) return "< 1h";
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
}

function getSeverityLabel(leak: Leak): string {
    const hasCritical = leak.data_types?.some((t) =>
        ["Credentials", "Financial", "PII"].includes(t)
    );
    return hasCritical ? "Critical" : "Medium";
}

/* ── main component ─────────────────────────────────────────────────────── */
export default function EngagementBoard() {
    const navigate = useNavigate();
    const [board, setBoard] = useState<Record<string, Leak[]>>({});
    const [loading, setLoading] = useState(true);
    const [dragItem, setDragItem] = useState<{
        leak: Leak;
        fromStage: EngagementStatus;
    } | null>(null);
    const [dragOverStage, setDragOverStage] = useState<EngagementStatus | null>(
        null
    );

    const loadBoard = useCallback(async () => {
        try {
            const data = await getEngagementBoard();
            setBoard(data);
        } catch (err) {
            console.error("Failed to load engagement board", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBoard();
    }, [loadBoard]);

    /* ── Drag & Drop handlers ─────────────────────────────────────────── */
    const handleDragStart = (leak: Leak, fromStage: EngagementStatus) => {
        setDragItem({ leak, fromStage });
    };

    const handleDragOver = (
        e: React.DragEvent,
        targetStage: EngagementStatus
    ) => {
        e.preventDefault();
        if (!dragItem) return;

        const allowed = ALLOWED_TRANSITIONS[dragItem.fromStage] || [];
        if (allowed.includes(targetStage)) {
            setDragOverStage(targetStage);
        }
    };

    const handleDragLeave = () => {
        setDragOverStage(null);
    };

    const handleDrop = async (
        e: React.DragEvent,
        targetStage: EngagementStatus
    ) => {
        e.preventDefault();
        setDragOverStage(null);

        if (!dragItem) return;

        const allowed = ALLOWED_TRANSITIONS[dragItem.fromStage] || [];
        if (!allowed.includes(targetStage)) {
            toast.error(
                `Cannot move from "${STAGE_LABELS[dragItem.fromStage]}" to "${STAGE_LABELS[targetStage]}"`
            );
            setDragItem(null);
            return;
        }

        try {
            await updateEngagementStatus(
                dragItem.leak._id,
                targetStage,
                `Moved via Kanban board`
            );
            toast.success(
                `Moved to "${STAGE_LABELS[targetStage]}"`
            );
            await loadBoard();
        } catch (err) {
            const detail =
                (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Failed to update engagement status";
            toast.error(detail);
        } finally {
            setDragItem(null);
        }
    };

    const handleDragEnd = () => {
        setDragItem(null);
        setDragOverStage(null);
    };

    if (loading) return <Skeleton rows={6} className="mt-8" />;

    return (
        <div className="engagement-board-page">
            {/* Header */}
            <div className="engagement-board-header">
                <h1 className="engagement-board-title">Engagement Board</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                        className="engagement-board-subtitle"
                        style={{
                            fontSize: "0.72rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <AlertTriangle size={12} />
                        Drag cards between adjacent columns to transition
                    </span>
                </div>
            </div>

            {/* Kanban Board */}
            <div className="kanban-board">
                {ALL_STAGES.map((stage) => {
                    const cards = board[stage] || [];
                    const isDragTarget =
                        dragItem &&
                        (ALLOWED_TRANSITIONS[dragItem.fromStage] || []).includes(stage);
                    const isOver = dragOverStage === stage;

                    return (
                        <div className="kanban-column" key={stage}>
                            <div className="kanban-column-header">
                                <span className="kanban-column-title">
                                    {STAGE_LABELS[stage]}
                                </span>
                                <span className="kanban-column-count">{cards.length}</span>
                            </div>

                            <div
                                className={`kanban-cards${isOver ? " kanban-cards--dragover" : ""}`}
                                onDragOver={(e) => handleDragOver(e, stage)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, stage)}
                                style={{
                                    border: isDragTarget
                                        ? "1px dashed rgba(0, 102, 224, 0.3)"
                                        : "1px dashed transparent",
                                    borderRadius: 8,
                                    transition: "border 0.2s ease",
                                }}
                            >
                                {cards.map((leak) => {
                                    const title =
                                        leak.thread_title ||
                                        leak.thread_title_list ||
                                        leak.title ||
                                        "Untitled Leak";
                                    const severity = getSeverityLabel(leak);
                                    const isDragging =
                                        dragItem?.leak._id === leak._id;

                                    return (
                                        <div
                                            key={leak._id}
                                            className={`kanban-card${isDragging ? " kanban-card--dragging" : ""}`}
                                            draggable
                                            onDragStart={() =>
                                                handleDragStart(
                                                    leak,
                                                    (leak.engagement_status as EngagementStatus) ||
                                                    "leak_detected"
                                                )
                                            }
                                            onDragEnd={handleDragEnd}
                                            onClick={() => navigate(`/leaks/${leak._id}`)}
                                        >
                                            <div className="kanban-card-title">{title}</div>
                                            <div className="kanban-card-meta">
                                                {leak.client_name && (
                                                    <span className="kanban-card-tag">
                                                        {leak.client_name}
                                                    </span>
                                                )}
                                                {leak.sectors?.slice(0, 1).map((s) => (
                                                    <span key={s} className="kanban-card-tag">
                                                        {s}
                                                    </span>
                                                ))}
                                                <span
                                                    className="kanban-card-tag"
                                                    style={{
                                                        background:
                                                            severity === "Critical"
                                                                ? "rgba(239,68,68,0.12)"
                                                                : "rgba(245,158,11,0.12)",
                                                        color:
                                                            severity === "Critical"
                                                                ? "#f87171"
                                                                : "#fbbf24",
                                                        borderColor:
                                                            severity === "Critical"
                                                                ? "rgba(239,68,68,0.25)"
                                                                : "rgba(245,158,11,0.25)",
                                                    }}
                                                >
                                                    {severity}
                                                </span>
                                            </div>
                                            <div className="kanban-card-time">
                                                <Clock size={10} />
                                                {timeAgo(leak.created_at)}
                                            </div>
                                        </div>
                                    );
                                })}

                                {cards.length === 0 && (
                                    <div
                                        className="kanban-empty-text"
                                        style={{
                                            padding: "20px 10px",
                                            textAlign: "center",
                                            fontSize: "0.72rem",
                                        }}
                                    >
                                        No items
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
